import classnames from 'classnames'
import { FormikProvider, useFormik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { CommentRootUuid } from '../../../models/Comment'
import useUpdateComment from '../../../state/mutations/useUpdateComment'
import useComment from '../../../state/queries/useComment'
import FormButtons from '../../basics/FormButtons/FormButtons'
import { TextAreaFormik } from '../../basics/TextArea/TextArea'
import { TextInputFormik } from '../../basics/TextInput/TextInput'

import styles from './EditCommentForm.module.scss'
import set from 'lodash/set'

type EditCommentFormProps = {
  commentUuid: string
  rootUuid: CommentRootUuid
  onClose: () => void
}

export default React.memo(function EditCommentForm({ onClose, commentUuid, rootUuid }: EditCommentFormProps) {
  const { t } = useTranslation()

  const { comment } = useComment({ commentUuid, ...rootUuid })

  const updateComment = useUpdateComment()
  const formik = useFormik({
    initialValues: {
      commentUuid,
      ...rootUuid,
      title: comment.isDummy ? '' : comment.title,
      text: comment.isDummy ? '' : comment.text,
    },
    validate: (values) => {
      const errors = {}

      if (values.text && values.text.length > 2048) {
        set(errors, `text`, t('errors.fields.message.not longer then', { number: 2048 }))
      }

      if (values.title && values.title.length > 255) {
        set(errors, `title`, t('errors.fields.message.not longer then', { number: 255 }))
      }

      return errors
    },
    enableReinitialize: true,
    onSubmit: async (values, { setStatus }) => {
      setStatus()
      try {
        await updateComment.mutateAsync({ ...(values as any) })
        onClose()
      } catch (err) {
        setStatus(String(err))
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={classnames(styles.EditCommentForm)}>
        <header>
          <hgroup>
            <h1>{t('comment.title edit comment')}</h1>
          </hgroup>
        </header>

        <div>
          {!comment.parentUuid && <TextInputFormik name="title" label={t('comment.field title')} required />}
          <TextAreaFormik name="text" label={t('comment.field text')} required />
        </div>

        <FormButtons onClose={onClose} {...formik} />
      </form>
    </FormikProvider>
  )
})
